"use client";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

import { useState, useCallback } from "react";
import Link from "next/link";
import Footer from "@/components/shared/Footer";

/* -- Constants --------------------------------------------------------- */

const SERVICE_OPTIONS = [
  "General Dentistry",
  "Cosmetic Dentistry",
  "Orthodontics",
  "Pediatric Dentistry",
  "Emergency Dental Care",
  "Dental Implants",
  "Teeth Whitening",
] as const;

/* -- Types ------------------------------------------------------------- */

interface FormData {
  practiceName: string;
  dentistName: string;
  phoneNumber: string;
  hours: string;
  services: string[];
  insurance: string;
  specialOffer: string;
}

interface GeneratedScripts {
  incomingCall: string;
  voicemail: string;
  afterHours: string;
}

/* -- Script generation (pure client-side) ------------------------------ */

function generateScripts(data: FormData): GeneratedScripts {
  const {
    practiceName,
    dentistName,
    phoneNumber,
    hours,
    services,
    insurance,
    specialOffer,
  } = data;

  const drName = dentistName || "[Dentist Name]";
  const practName = practiceName || "[Practice Name]";
  const phone = phoneNumber || "[Phone Number]";
  const officeHours = hours || "[Office Hours]";
  const servicesList = services.length > 0 ? services.join(", ") : "[Services]";
  const insuranceList = insurance || "[Insurance Plans]";

  // Script 1: Incoming Call Greeting
  let incomingCall = `Thank you for calling ${practName}, this is [front desk name]. How can I help you today?

`;
  incomingCall += `[If new patient]
"Welcome! We'd love to have you as a patient. Dr. ${drName} is accepting new patients.`;
  if (specialOffer) {
    incomingCall += ` We're currently offering ${specialOffer} for new patients.`;
  }
  incomingCall += `"

`;
  incomingCall += `[If scheduling]
"I'd be happy to schedule that for you. We're open ${officeHours}. What day and time works best for you?"

`;
  incomingCall += `[If insurance question]
"Yes, we accept ${insuranceList}. Would you like me to verify your benefits before your appointment?"

`;
  incomingCall += `[If emergency]
"I'm sorry to hear that. Dr. ${drName} sees emergency patients. Can you describe what's happening so I can prioritise your appointment?"

`;
  incomingCall += `"Is there anything else I can help you with today? Thank you for calling ${practName}!"`;

  // Script 2: Voicemail Greeting
  let voicemail = `"You've reached ${practName}, the office of Dr. ${drName}. Our office hours are ${officeHours}.`;
  if (specialOffer) {
    voicemail += ` We're currently offering ${specialOffer} for new patients.`;
  }
  voicemail += ` Please leave your name, phone number, and a brief message, and we'll return your call within one business day. If this is a dental emergency, please call ${phone} or visit your nearest emergency room. Thank you and we look forward to hearing from you!"`;

  // Script 3: After-Hours Auto-Reply
  let afterHours = `"Hi! Thank you for reaching out to ${practName}. Our office is currently closed. Our hours are ${officeHours}. We offer ${servicesList}.`;
  if (specialOffer) {
    afterHours += ` New patients: ask about our ${specialOffer}!`;
  }
  afterHours += ` Leave your name and contact info, and we'll get back to you first thing. If this is an emergency, please call ${phone}."`;

  return { incomingCall, voicemail, afterHours };
}

/* -- Copy button component --------------------------------------------- */

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
        copied
          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
          : "bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200"
      }`}
    >
      {copied ? (
        <>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Copied!
        </>
      ) : (
        <>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          Copy to Clipboard
        </>
      )}
    </button>
  );
}

/* -- Script card component --------------------------------------------- */

function ScriptCard({
  title,
  icon,
  script,
  colorClasses,
}: {
  title: string;
  icon: string;
  script: string;
  colorClasses: {
    border: string;
    bg: string;
    badge: string;
    badgeText: string;
    iconBg: string;
  };
}) {
  return (
    <div className={`rounded-xl border-2 ${ colorClasses.border} ${ colorClasses.bg} overflow-hidden`}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${ colorClasses.iconBg} rounded-lg flex items-center justify-center text-lg`}>
              {icon}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
          </div>
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${ colorClasses.badge} ${ colorClasses.badgeText}`}>
            Ready to Use
          </span>
        </div>
        <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 leading-relaxed font-sans bg-white/60 dark:bg-gray-800/60 rounded-lg p-4 mb-4 max-h-80 overflow-y-auto">
          {script}
        </pre>
        <CopyButton text={script} />
      </div>
    </div>
  );
}

/* -- Main client component --------------------------------------------- */

export default function ScriptGeneratorClient() {
  const [formData, setFormData] = useState<FormData>({
    practiceName: "",
    dentistName: "",
    phoneNumber: "",
    hours: "",
    services: [],
    insurance: "",
    specialOffer: "",
  });

  const [scripts, setScripts] = useState<GeneratedScripts | null>(null);
  const [showScripts, setShowScripts] = useState(false);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleServiceToggle = (service: string) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter((s) => s !== service)
        : [...prev.services, service],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const generated = generateScripts(formData);
    setScripts(generated);
    setShowScripts(true);

    // GA4: Track script_generated
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "script_generated", {
        event_category: "conversion",
        event_label: formData.practiceName || "unnamed",
      });
    }

    setTimeout(() => {
      document
        .getElementById("generated-scripts")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      {/* Navigation */}
      <nav className="border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">AL</span>
            </div>
            <span className="text-xl font-bold text-gray-900 dark:text-white">
              Aditya Labs
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              href="/"
              className="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition"
            >
              Home
            </Link>
            <Link
              href="/tools/free-ai-dental-receptionist"
              className="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition"
            >
              Free Tools
            </Link>
            <Link
              href="/pricing"
              className="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition"
            >
              Pricing
            </Link>
            <Link
              href="/signup"
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
            >
              Start Free
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm font-medium px-4 py-1.5 rounded-full mb-6">
            <span className="text-lg">&#128196;</span>
            Free Tool &mdash; No Sign-Up Required
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white leading-tight mb-6">
            Free Dental Reception Script Generator
          </h1>

          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Generate professional phone scripts for your dental practice in 30
            seconds. Enter your clinic details and get ready-to-use scripts for
            answering calls, voicemail, and after-hours chat.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <form
            onSubmit={handleSubmit}
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 shadow-sm"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Your Practice Details
            </h2>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Practice Name */}
              <div>
                <label
                  htmlFor="practiceName"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                >
                  Practice Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="practiceName"
                  type="text"
                  required
                  placeholder="e.g., Bright Smile Dental"
                  value={formData.practiceName}
                  onChange={(e) => handleInputChange("practiceName", e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
              </div>

              {/* Dentist Name */}
              <div>
                <label
                  htmlFor="dentistName"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                >
                  Dentist Name
                </label>
                <input
                  id="dentistName"
                  type="text"
                  placeholder="e.g., Sarah Johnson"
                  value={formData.dentistName}
                  onChange={(e) => handleInputChange("dentistName", e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
              </div>

              {/* Phone Number */}
              <div>
                <label
                  htmlFor="phoneNumber"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                >
                  Phone Number
                </label>
                <input
                  id="phoneNumber"
                  type="text"
                  placeholder="e.g., (555) 123-4567"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
              </div>

              {/* Hours */}
              <div>
                <label
                  htmlFor="hours"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                >
                  Office Hours
                </label>
                <input
                  id="hours"
                  type="text"
                  placeholder="e.g., Mon-Fri 8am-5pm"
                  value={formData.hours}
                  onChange={(e) => handleInputChange("hours", e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
              </div>
            </div>

            {/* Services Offered */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Services Offered
              </label>
              <div className="flex flex-wrap gap-3">
                {SERVICE_OPTIONS.map((service) => (
                  <label
                    key={service}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition text-sm ${
                      formData.services.includes(service)
                        ? "bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300"
                        : "bg-white border-gray-300 text-gray-700 hover:border-gray-400 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:border-gray-600"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.services.includes(service)}
                      onChange={() => handleServiceToggle(service)}
                      className="sr-only"
                    />
                    <span
                      className={`w-4 h-4 rounded border flex items-center justify-center ${
                        formData.services.includes(service)
                          ? "bg-blue-600 border-blue-600"
                          : "border-gray-400 dark:border-gray-500"
                      }`}
                    >
                      {formData.services.includes(service) && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </span>
                    {service}
                  </label>
                ))}
              </div>
            </div>

            {/* Insurance Accepted */}
            <div className="mb-6">
              <label
                htmlFor="insurance"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
              >
                Insurance Accepted
              </label>
              <input
                id="insurance"
                type="text"
                placeholder="e.g., Aetna, Delta Dental, Cigna, MetLife"
                value={formData.insurance}
                onChange={(e) => handleInputChange("insurance", e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
              />
            </div>

            {/* Special Offer */}
            <div className="mb-8">
              <label
                htmlFor="specialOffer"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
              >
                Special Offer for New Patients{" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="specialOffer"
                type="text"
                placeholder="e.g., $99 New Patient Exam & X-rays"
                value={formData.specialOffer}
                onChange={(e) => handleInputChange("specialOffer", e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold px-8 py-4 rounded-lg text-lg transition shadow-lg inline-flex items-center justify-center gap-2"
            >
              Generate My Scripts
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </button>
          </form>
        </div>
      </section>

      {/* Generated Scripts Section */}
      {showScripts && scripts && (
        <section id="generated-scripts" className="scroll-mt-8 pb-20 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
                Your Custom Scripts
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Copy each script and share it with your front desk team.
                Customise the bracketed sections as needed.
              </p>
            </div>

            <div className="space-y-8">
              {/* Script 1: Incoming Call */}
              <ScriptCard
                title="Incoming Call Greeting"
                icon={"📞"}
                script={scripts.incomingCall}
                colorClasses={{
                  border: "border-blue-200 dark:border-blue-800",
                  bg: "bg-blue-50/50 dark:bg-blue-950/30",
                  badge: "bg-blue-100 dark:bg-blue-900/50",
                  badgeText: "text-blue-700 dark:text-blue-300",
                  iconBg: "bg-blue-100 dark:bg-blue-900/50",
                }}
              />

              {/* Script 2: Voicemail */}
              <ScriptCard
                title="Voicemail Greeting"
                icon={"📤"}
                script={scripts.voicemail}
                colorClasses={{
                  border: "border-purple-200 dark:border-purple-800",
                  bg: "bg-purple-50/50 dark:bg-purple-950/30",
                  badge: "bg-purple-100 dark:bg-purple-900/50",
                  badgeText: "text-purple-700 dark:text-purple-300",
                  iconBg: "bg-purple-100 dark:bg-purple-900/50",
                }}
              />

              {/* Script 3: After-Hours */}
              <ScriptCard
                title="After-Hours Auto-Reply (Website Chat)"
                icon={"💬"}
                script={scripts.afterHours}
                colorClasses={{
                  border: "border-emerald-200 dark:border-emerald-800",
                  bg: "bg-emerald-50/50 dark:bg-emerald-950/30",
                  badge: "bg-emerald-100 dark:bg-emerald-900/50",
                  badgeText: "text-emerald-700 dark:text-emerald-300",
                  iconBg: "bg-emerald-100 dark:bg-emerald-900/50",
                }}
              />
            </div>
          </div>
        </section>
      )}

      {/* CTA Section - shown after scripts */}
      {showScripts && (
        <section className="py-20 px-6 bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Don&apos;t want to read scripts?
            </h2>
            <p className="text-lg text-blue-100 mb-3">
              Let AI handle your calls and website chat automatically.
            </p>
            <p className="text-sm text-blue-200 mb-8">
              Your AI agent will respond to patients 24/7 using these scripts
              &mdash; and more. No typing required.
            </p>
            <Link
              href="/signup"
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 text-blue-600 font-semibold px-8 py-4 rounded-lg text-lg transition shadow-lg"
            >
              Try Aditya Labs Free
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </Link>
          </div>
        </section>
      )}

      {/* Why Use This Tool - always visible */}
      <section className="py-20 px-6 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Why Your Practice Needs Professional Scripts
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Consistent phone scripts help your team deliver a great patient
              experience on every call.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "First Impressions Matter",
                desc: "Patients decide within seconds whether they trust your practice. A professional greeting sets the tone for the entire relationship.",
                icon: "⭐",
              },
              {
                title: "Reduce Missed Opportunities",
                desc: "Over one-third of calls to dental offices go unanswered. Having scripts ready ensures every answered call converts to a booked appointment.",
                icon: "📈",
              },
              {
                title: "Train Staff Faster",
                desc: "New front desk hires get up to speed in hours instead of weeks when they have clear, tested scripts to follow.",
                icon: "🚀",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
              >
                <div className="text-3xl mb-4">{item.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Want an AI Agent That Answers Calls 24/7?
          </h2>
          <p className="text-lg text-blue-100 mb-8">
            Scripts are great for training staff &mdash; but an AI agent never
            forgets a line, never takes a break, and never misses a call.
          </p>
          <Link
            href="/signup"
            className="inline-block bg-white hover:bg-gray-100 text-blue-600 font-semibold px-8 py-4 rounded-lg text-lg transition shadow-lg"
          >
            Start Free &mdash; No Credit Card Required
          </Link>
          <p className="text-blue-200 text-sm mt-4">
            Guided setup available. Review pricing and terms before upgrading.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
